<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { NodeViewWrapper } from '@tiptap/vue-3';
import type { Editor } from '@tiptap/vue-3';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { ChevronDown, ChevronRight, X } from 'lucide-vue-next';
import type { VariableType } from './extensions/variable-node';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { Input } from '../input';
import { Label } from '../label';
import { Button } from '../button';

const SLOT_NAME_RE = /^[a-z][a-z0-9_]*$/;

const props = defineProps<{
  node: ProseMirrorNode;
  editor: Editor;
  deleteNode: () => void;
  updateAttributes: (attrs: Record<string, unknown>) => void;
}>();

const open = ref(false);
const draftName = ref('');
const draftDefault = ref('');
const nameError = ref('');
/** Fragments default to expanded so authors see the injected prompt content. */
const fragmentExpanded = ref(true);

const variableType = computed<VariableType>(
  () => props.node.attrs.type || 'system',
);
const variableName = computed(() => props.node.attrs.name || '');
const defaultValue = computed(() => {
  const value = props.node.attrs.defaultValue;
  return value == null ? null : String(value);
});
const preview = computed(() => {
  const value = props.node.attrs.preview;
  return value == null || value === '' ? null : String(value);
});
const isEditable = computed(() => props.editor.isEditable);
const isSlot = computed(() => variableType.value === 'slot');
const isFragment = computed(() => variableType.value === 'fragment');
const canEditSlot = computed(() => isSlot.value && isEditable.value);

const displayText = computed(() => {
  const type = variableType.value;
  const name = variableName.value;

  if (type === 'system') {
    return name;
  }
  return `${type}:${name}`;
});

const truncatedDefault = computed(() => {
  const value = defaultValue.value;
  if (value == null) return null;
  const display = value.length > 24 ? `${value.slice(0, 24)}…` : value;
  return display;
});

const tooltipContent = computed(() => {
  const type = variableType.value;
  const name = variableName.value;
  const previewText = preview.value;

  if (type === 'fragment') {
    return fragmentExpanded.value
      ? `Fragment: ${name} (click to collapse)`
      : `Fragment: ${name} (click to expand)`;
  }

  if (type === 'slot') {
    const lines = [`Slot: ${name}`];
    if (defaultValue.value != null) {
      lines.push(`Default: ${defaultValue.value}`);
    }
    if (previewText) {
      lines.push(`Value: ${previewText}`);
    }
    if (canEditSlot.value) {
      lines.push('', 'Click to edit');
    }
    return lines.join('\n');
  }

  return `System variable: ${name}`;
});

const badgeClasses = computed(() => {
  const type = variableType.value;

  const baseClasses =
    'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium select-none group max-w-full';

  const cursorClass =
    canEditSlot.value || isFragment.value
      ? 'cursor-pointer'
      : 'cursor-default';

  switch (type) {
    case 'fragment':
      return `${baseClasses} ${cursorClass} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
    case 'slot':
      return `${baseClasses} ${cursorClass} bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200`;
    case 'system':
    default:
      return `${baseClasses} ${cursorClass} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
  }
});

const deleteButtonClasses = computed(() => {
  const type = variableType.value;

  const baseClasses =
    'rounded-sm hover:bg-black/10 dark:hover:bg-white/10 p-0.5 -mr-0.5';

  switch (type) {
    case 'fragment':
      return `${baseClasses} text-green-700 dark:text-green-300`;
    case 'slot':
      return `${baseClasses} text-purple-700 dark:text-purple-300`;
    case 'system':
    default:
      return `${baseClasses} text-blue-700 dark:text-blue-300`;
  }
});

watch(open, (isOpen) => {
  if (isOpen) {
    draftName.value = variableName.value;
    draftDefault.value = defaultValue.value ?? '';
    nameError.value = '';
  }
});

function applySlotEdits() {
  const name = draftName.value.trim();
  if (!SLOT_NAME_RE.test(name)) {
    nameError.value =
      'Must be snake_case starting with a letter (e.g. my_slot)';
    return;
  }

  const trimmedDefault = draftDefault.value;
  props.updateAttributes({
    name,
    // null clears the attr so serialization omits | default
    defaultValue: trimmedDefault.length > 0 ? trimmedDefault : null,
  });
  open.value = false;
}

function clearDefault() {
  draftDefault.value = '';
}

function onDelete(event: Event) {
  event.stopPropagation();
  event.preventDefault();
  open.value = false;
  props.deleteNode();
}

function toggleFragment(event?: Event) {
  event?.stopPropagation();
  event?.preventDefault();
  fragmentExpanded.value = !fragmentExpanded.value;
}
</script>

<template>
  <NodeViewWrapper
    as="span"
    :class="[
      'variable-badge-wrapper',
      isFragment ? 'variable-badge-wrapper--fragment' : '',
    ]"
  >
    <!-- Expandable fragment with green content preview -->
    <span
      v-if="isFragment"
      class="fragment-expandable bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
      :class="fragmentExpanded ? 'fragment-expandable--open' : ''"
      contenteditable="false"
      @mousedown.prevent
    >
      <span
        class="fragment-header"
        :class="
          fragmentExpanded
            ? 'w-full border-b border-green-200/80 dark:border-green-700/80'
            : ''
        "
        :title="tooltipContent"
        role="button"
        tabindex="0"
        @click="toggleFragment"
        @keydown.enter.prevent="toggleFragment"
        @keydown.space.prevent="toggleFragment"
      >
        <component
          :is="fragmentExpanded ? ChevronDown : ChevronRight"
          class="h-3 w-3 shrink-0 opacity-80"
        />
        <span class="truncate font-medium text-xs">{{ displayText }}</span>
        <button
          v-if="isEditable"
          type="button"
          :class="deleteButtonClasses"
          @click="onDelete"
          @mousedown.prevent
        >
          <X class="h-3 w-3" />
        </button>
      </span>
      <span v-if="fragmentExpanded" class="fragment-content">
        <span
          v-if="preview"
          class="fragment-content-text whitespace-pre-wrap break-words"
          >{{ preview }}</span
        >
        <span v-else class="fragment-content-text italic opacity-70"
          >Fragment content unavailable</span
        >
      </span>
    </span>

    <!-- Editable slot chip -->
    <Popover v-else-if="canEditSlot" v-model:open="open">
      <PopoverTrigger as-child>
        <span
          :class="badgeClasses"
          :title="tooltipContent"
          contenteditable="false"
          @mousedown.prevent
        >
          <span class="truncate">{{ displayText }}</span>
          <span
            v-if="truncatedDefault !== null"
            class="truncate opacity-70 font-normal max-w-[10rem]"
            :title="defaultValue ?? undefined"
          >
            = "{{ truncatedDefault }}"
          </span>
          <button
            type="button"
            :class="deleteButtonClasses"
            @click="onDelete"
            @mousedown.prevent
          >
            <X class="h-3 w-3" />
          </button>
        </span>
      </PopoverTrigger>
      <PopoverContent
        class="w-80 p-3"
        align="start"
        @open-auto-focus.prevent
      >
        <div class="grid gap-3">
          <div class="grid gap-1.5">
            <Label class="text-xs">Slot name</Label>
            <Input
              v-model="draftName"
              class="h-8 text-sm font-mono"
              placeholder="my_slot_name"
              @keydown.enter.prevent="applySlotEdits"
            />
            <p v-if="nameError" class="text-xs text-red-500">{{ nameError }}</p>
          </div>
          <div class="grid gap-1.5">
            <div class="flex items-center justify-between gap-2">
              <Label class="text-xs">Default value</Label>
              <button
                v-if="draftDefault.length > 0"
                type="button"
                class="text-xs text-muted-foreground hover:text-foreground"
                @click="clearDefault"
              >
                Clear
              </button>
            </div>
            <textarea
              v-model="draftDefault"
              rows="3"
              class="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder="Used when the instance leaves this slot empty"
            />
          </div>
          <div class="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              @click="open = false"
            >
              Cancel
            </Button>
            <Button type="button" size="sm" @click="applySlotEdits">
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>

    <!-- System / read-only slot chips -->
    <span
      v-else
      :class="badgeClasses"
      :title="tooltipContent"
      contenteditable="false"
    >
      <span class="truncate">{{ displayText }}</span>
      <span
        v-if="isSlot && truncatedDefault !== null"
        class="truncate opacity-70 font-normal max-w-[10rem]"
        :title="defaultValue ?? undefined"
      >
        = "{{ truncatedDefault }}"
      </span>
      <button
        v-if="isEditable"
        type="button"
        :class="deleteButtonClasses"
        @click.stop.prevent="props.deleteNode"
        @mousedown.prevent
      >
        <X class="h-3 w-3" />
      </button>
    </span>
  </NodeViewWrapper>
</template>

<style scoped>
.variable-badge-wrapper {
  display: inline;
  line-height: inherit;
}

.variable-badge-wrapper--fragment {
  display: inline-block;
  max-width: 100%;
  vertical-align: top;
  margin: 0.25rem 0;
}

.fragment-expandable {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: fit-content;
  max-width: 100%;
  border-radius: 0.375rem;
  overflow: hidden;
}

.fragment-expandable--open {
  width: 100%;
}

.fragment-header {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.375rem;
  cursor: pointer;
  user-select: none;
  max-width: 100%;
  box-sizing: border-box;
}

.fragment-content {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.625rem;
  font-size: 0.8125rem;
  line-height: 1.4;
  font-weight: 400;
}

.fragment-content-text {
  display: block;
}
</style>
